import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useQueryClient } from 'react-query';
import { subscriptionKeys } from '../../components/Subscription/useSubscriptionQueries';
import './subscription.css';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const [loading, setLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!sessionId) {
      toast.error('Invalid session. Redirecting...', { position: "bottom-right", autoClose: 2000 });
      setTimeout(() => navigate('/user/subscription'), 2000);
      return;
    }

    // Verify the session and get subscription details
    const verifySession = async () => {
      try {
        if (!token) {
          toast.error('Please log in to view subscription details', { position: "bottom-right", autoClose: 2000 });
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        // Invalidate and refetch subscription queries to get updated data
        await queryClient.invalidateQueries(subscriptionKeys.activeSubscription());
        await queryClient.invalidateQueries(subscriptionKeys.payments());
        await queryClient.invalidateQueries(subscriptionKeys.plans());
        
        setSubscriptionData({ sessionId });
        setLoading(false);
        
        toast.success('Subscription activated successfully!', { 
          position: "bottom-right", 
          autoClose: 3000 
        });
      } catch (error) {
        console.error('Error verifying session:', error);
        toast.error('Error verifying subscription. Please contact support.', { 
          position: "bottom-right", 
          autoClose: 3000 
        });
        setLoading(false);
      }
    };

    verifySession();
  }, [sessionId, token, navigate, queryClient]);

  const handleGoToSubscription = () => {
    // Navigate to subscription pricing page or profile settings subscription tab
    navigate('/user/profile/settings');
  };

  const handleGoToDashboard = () => {
    navigate('/user/dashboard');
  };

  if (loading) {
    return (
      <div className="subscription-success-page">
        <div className="subscription-success-container">
          <div className="subscription-success-loader">
            <div className="spinner"></div>
            <p>Verifying your subscription...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-success-page">
      <div className="subscription-success-container">
        <div className="subscription-success-content">
          <div className="success-icon">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="40" cy="40" r="40" fill="#10B981" opacity="0.1"/>
              <path d="M40 20C28.9543 20 20 28.9543 20 40C20 51.0457 28.9543 60 40 60C51.0457 60 60 51.0457 60 40C60 28.9543 51.0457 20 40 20ZM35 47.5L27.5 40L30.175 37.325L35 42.15L49.825 27.325L52.5 30L35 47.5Z" fill="#10B981"/>
            </svg>
          </div>
          <h1>Payment Successful!</h1>
          <p className="success-message">
            Your subscription has been activated successfully. You now have access to all premium features.
          </p>
          {subscriptionData && (
            <div className="subscription-details">
              <p className="session-id">Session ID: {subscriptionData.sessionId}</p>
            </div>
          )}
          <div className="success-actions">
            <button 
              className="btn-primary" 
              onClick={handleGoToSubscription}
            >
              View Subscription
            </button>
            <button 
              className="btn-secondary" 
              onClick={handleGoToDashboard}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
      <style>{`
        .subscription-success-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px;
        }
        .subscription-success-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          max-width: 600px;
          width: 100%;
          padding: 40px;
          text-align: center;
        }
        .subscription-success-loader {
          padding: 40px;
        }
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .subscription-success-content {
          animation: fadeIn 0.5s ease-in;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .success-icon {
          margin-bottom: 24px;
        }
        .subscription-success-container h1 {
          color: #1f2937;
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 16px;
        }
        .success-message {
          color: #6b7280;
          font-size: 18px;
          line-height: 1.6;
          margin-bottom: 24px;
        }
        .subscription-details {
          background: #f9fafb;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 32px;
        }
        .session-id {
          color: #6b7280;
          font-size: 14px;
          font-family: monospace;
          word-break: break-all;
        }
        .success-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .btn-primary, .btn-secondary {
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }
        .btn-primary {
          background: #667eea;
          color: white;
        }
        .btn-primary:hover {
          background: #5568d3;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }
        .btn-secondary {
          background: white;
          color: #667eea;
          border: 2px solid #667eea;
        }
        .btn-secondary:hover {
          background: #f3f4f6;
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
};

export default SubscriptionSuccess;

